import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Logo from '../Logo';
import {
    Menu,
    X,
    Home,
    Users,
    UserCheck,
    FileText,
    BarChart3,
    LogOut,
    Shield
} from 'lucide-react';

const AdminLayout = ({ children, title }) => {
    const { admin, logout, isSuperAdmin } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(false);

    const handleLogout = () => {
        logout();
        navigate('/admin/login');
    };

    const menuItems = [
        { path: '/admin/dashboard', icon: Home, label: 'Dashboard' },
        { path: '/admin/students', icon: Users, label: 'Students' },
        { path: '/admin/assessments', icon: FileText, label: 'Assessments' },
        { path: '/admin/results', icon: BarChart3, label: 'Results' },
        ...(isSuperAdmin ? [{ path: '/admin/admins', icon: Shield, label: 'Admins' }] : [])
    ];

    return (
        <div className="admin-layout">
            {/* Sidebar */}
            <div className={`sidebar ${sidebarOpen ? 'sidebar-open' : ''}`}>
                <div className="sidebar-header">
                    <div className="branding">
                        <Logo size="medium" />
                        <h3>Admin Portal</h3>
                    </div>
                </div>

                <nav className="sidebar-nav">
                    {menuItems.map((item) => {
                        const Icon = item.icon;
                        const isActive = location.pathname === item.path;

                        return (
                            <Link
                                key={item.path}
                                to={item.path}
                                className={`nav-item ${isActive ? 'active' : ''}`}
                                onClick={() => setSidebarOpen(false)}
                            >
                                <Icon size={20} />
                                <span>{item.label}</span>
                            </Link>
                        );
                    })}
                </nav>

                <div className="sidebar-footer">
                    <div className="admin-info">
                        <div className="admin-name">{admin?.name}</div>
                        <div className="admin-role">{admin?.role === 'super_admin' ? 'Super Admin' : 'Admin'}</div>
                    </div>
                    <button className="logout-btn" onClick={handleLogout}>
                        <LogOut size={20} />
                        <span>Logout</span>
                    </button>
                </div>
            </div>

            {/* Main Content */}
            <div className="main-content">
                {/* Header */}
                <header className="main-header">
                    <button
                        className="sidebar-toggle"
                        onClick={() => setSidebarOpen(!sidebarOpen)}
                    >
                        {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
                    </button>

                    <h1 className="page-title">{title}</h1>

                    <div className="header-actions">
                        <div className="admin-badge">
                            <UserCheck size={20} />
                            <span>{admin?.name}</span>
                        </div>
                    </div>
                </header>

                {/* Page Content */}
                <main className="page-content">
                    {children}
                </main>
            </div>

            {/* Sidebar Overlay */}
            {sidebarOpen && (
                <div
                    className="sidebar-overlay"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            <style jsx>{`
        .admin-layout {
          display: flex;
          min-height: 100vh;
          background-color: var(--background);
        }

        .sidebar {
          width: 280px;
          background-color: var(--card);
          box-shadow: var(--shadow-md);
          display: flex;
          flex-direction: column;
          border-right: 1px solid var(--border);
          position: fixed;
          height: 100vh;
          left: 0;
          top: 0;
          z-index: 100;
          transform: translateX(-100%);
          transition: transform 0.3s ease;
        }

        .sidebar.sidebar-open {
          transform: translateX(0);
        }

        .sidebar-header {
          padding: 24px;
          border-bottom: 1px solid var(--border);
          display: flex;
          justify-content: center;
        }

        .branding {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
          text-align: center;
        }

        .sidebar-header h3 {
          color: var(--foreground);
          font-size: 16px;
          font-weight: 600;
          margin: 0;
          font-family: 'Plus Jakarta Sans', sans-serif;
        }

        .sidebar-nav {
          flex: 1;
          padding: 16px;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .nav-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          color: var(--muted-foreground);
          text-decoration: none;
          border-radius: var(--border-radius);
          transition: var(--transition);
          font-weight: 500;
        }

        .nav-item:hover {
          color: var(--foreground);
          background-color: var(--muted);
        }

        .nav-item.active {
          color: var(--primary-foreground);
          background-color: var(--primary);
          box-shadow: var(--shadow-sm);
        }

        .sidebar-footer {
          padding: 16px;
          border-top: 1px solid var(--border);
        }

        .admin-info {
          padding: 12px;
          background-color: var(--muted);
          border-radius: var(--border-radius);
          margin-bottom: 12px;
        }

        .admin-name {
          font-weight: 600;
          color: var(--foreground);
          font-size: 14px;
        }

        .admin-role {
          font-size: 12px;
          color: var(--muted-foreground);
        }

        .logout-btn {
          width: 100%;
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          background: none;
          border: none;
          color: var(--muted-foreground);
          border-radius: var(--border-radius);
          transition: var(--transition);
          cursor: pointer;
          font-weight: 500;
        }

        .logout-btn:hover {
          color: var(--foreground);
          background-color: var(--muted);
        }

        .main-content {
          flex: 1;
          display: flex;
          flex-direction: column;
          margin-left: 0;
          height: 100vh;
          overflow: hidden;
        }

        .main-header {
          background-color: var(--card);
          border-bottom: 1px solid var(--border);
          padding: 16px 24px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          height: 80px;
          flex-shrink: 0;
          z-index: 50;
        }

        .sidebar-toggle {
          background: none;
          border: none;
          color: var(--foreground);
          cursor: pointer;
          padding: 8px;
          border-radius: var(--border-radius);
          transition: var(--transition);
        }

        .sidebar-toggle:hover {
          background-color: var(--muted);
        }

        .page-title {
          font-size: 24px;
          font-weight: 700;
          color: var(--foreground);
          margin: 0;
          flex: 1;
          margin-left: 16px;
        }

        .header-actions {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .admin-badge {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 16px;
          background-color: var(--muted);
          border-radius: var(--border-radius);
          font-size: 14px;
          font-weight: 500;
          color: var(--foreground);
        }

        .page-content {
          flex: 1;
          padding: 24px;
          background-color: var(--background);
          overflow-y: auto;
          height: calc(100vh - 80px); /* Subtract header height */
        }

        .sidebar-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          z-index: 99;
        }

        /* Desktop styles */
        @media (min-width: 1024px) {
          .admin-layout {
            overflow: hidden;
          }

          .sidebar {
            position: fixed;
            transform: none;
            height: 100vh;
            overflow-y: auto;
          }

          .sidebar-toggle {
            display: none;
          }

          .sidebar-overlay {
            display: none;
          }

          .main-content {
            margin-left: 280px;
            width: calc(100% - 280px);
            height: 100vh;
          }
        }

        /* Mobile styles */
        @media (max-width: 1023px) {
          .main-content {
            width: 100%;
            margin-left: 0;
          }
        }
      `}</style>
        </div>
    );
};

export default AdminLayout;